import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Receivables from './pages/Receivables';
import Loans from './pages/Loans';
import Taxes from './pages/Taxes';
import Reports from './pages/Reports';
import AuditLog from './pages/AuditLog';
import Reconciliation from './pages/Reconciliation';
import BankImport from './pages/BankImport';
import ReceiptScanner from './pages/ReceiptScanner';
import Currency from './pages/Currency';
import Settings from './pages/Settings';
import TeamManagement from './pages/TeamManagement';
import PlatformAdmin from './pages/PlatformAdmin';

// Layout
import Layout from './components/Layout';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--surface-2))]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-500))]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--surface-2))]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-500))]"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="receivables" element={<Receivables />} />
            <Route path="loans" element={<Loans />} />
            <Route path="taxes" element={<Taxes />} />
            <Route path="reports" element={<Reports />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="reconciliation" element={<Reconciliation />} />
            <Route path="bank-import" element={<BankImport />} />
            <Route path="receipt-scanner" element={<ReceiptScanner />} />
            <Route path="currency" element={<Currency />} />
            <Route path="settings" element={<Settings />} />
            <Route path="team" element={<TeamManagement />} />
            <Route path="platform-admin" element={<PlatformAdmin />} />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
